import { useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, Clock3, UserRound, X } from "lucide-react";
import {
  NOT_TODAY_REASON_OPTIONS,
  normalizeNotTodayReasonCode,
} from "@/lib/noPaymentVisit";

const ICONS = {
  medical: AlertTriangle,
  patient: UserRound,
  time: Clock3,
  other: CalendarClock,
};

export default function NotTodayReasonDialog({
  open,
  procedureName = "this treatment",
  initialReasonCode = null,
  onCancel,
  onConfirm,
  testIdPrefix = "not-today",
}) {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (open) setSelected(normalizeNotTodayReasonCode(initialReasonCode));
  }, [initialReasonCode, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 sm:items-center sm:p-4"
      data-testid={`${testIdPrefix}-dialog`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
    >
      <div className="w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p id={`${testIdPrefix}-title`} className="text-base font-bold text-slate-900">
              Why wasn’t this done today?
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {procedureName}. Pick one reason so reception knows what happens next.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close reason picker"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid gap-2">
          {NOT_TODAY_REASON_OPTIONS.map((option) => {
            const Icon = ICONS[option.code] || CalendarClock;
            const active = selected === option.code;
            return (
              <button
                key={option.code}
                type="button"
                onClick={() => setSelected(option.code)}
                data-testid={`${testIdPrefix}-reason-${option.code}`}
                className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                  active
                    ? "border-teal-500 bg-teal-50 ring-1 ring-teal-500"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
                  active ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-500"
                }`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                  <span className="block text-xs leading-5 text-slate-500">{option.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        {selected === "medical" ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
            Add the clinical details and next instructions in the Visit note before handing the patient to reception.
          </div>
        ) : null}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => selected && onConfirm?.(selected)}
            disabled={!selected}
            data-testid={`${testIdPrefix}-confirm`}
            className="flex-[1.4] rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Save Not today
          </button>
        </div>
      </div>
    </div>
  );
}
